import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  ShoppingCart,
  Plus,
  ExternalLink,
  Edit,
  Eye,
  Globe,
  Webhook,
  CheckCircle,
  XCircle,
  RefreshCw,
  Users,
  User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/DataTable'
import { SaleShop } from '@/types'
import { saleShopService } from '@/services/saleShopService'
import { formatDistanceToNow } from 'date-fns'
import { TableColumn, TableAction, TablePagination, TableSort, TableFilter } from '@/types/table'

export default function AdminSaleChannels() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [saleShops, setSaleShops] = useState<SaleShop[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [pagination, setPagination] = useState<TablePagination>({
    current: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [5, 10, 20, 50],
  })
  const [sort, setSort] = useState<TableSort | undefined>({
    key: 'created_at',
    direction: 'desc',
  })
  const [filters, setFilters] = useState<TableFilter[]>([])

  // Initial data fetch
  useEffect(() => {
    fetchSaleShops({
      page: pagination.current,
      pageSize: pagination.pageSize,
      sort,
      filters,
      search: '',
    })
  }, [])

  const fetchSaleShops = async (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      // Convert filters to API parameters
      const statusFilter = filters.find(f => f.key === 'is_active')
      const ownerFilter = filters.find(f => f.key === 'owner')
      const roleFilter = filters.find(f => f.key === 'role')

      const response = await saleShopService.getAdminSaleShops({
        page: params.page,
        page_size: params.pageSize,
        search: params.search || undefined,
        is_active: statusFilter ? statusFilter.value : undefined,
        owner: ownerFilter ? ownerFilter.value : undefined,
        role: roleFilter ? roleFilter.value : undefined,
      })

      if (response.success) {
        setSaleShops(response.data.results)
        setTotalCount(response.data.count)
        setPagination(prev => ({
          ...prev,
          current: params.page,
          total: response.data.count,
        }))
        return { data: response.data.results, total: response.data.count }
      } else {
        setError(response.message || 'Failed to fetch sale shops')
        return { data: [], total: 0 }
      }
    } catch (error: any) {
      const errorMessage = saleShopService.handleError
        ? saleShopService.handleError(error)
        : 'Failed to fetch sale shops'
      setError(errorMessage)
      console.error('Failed to fetch sale shops:', error)
      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }

  // Define table columns
  const columns: TableColumn<SaleShop>[] = [
    {
      key: 'name',
      title: 'Channel',
      dataIndex: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div className="flex items-center space-x-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" style={pageStyles.text}>{record.name}</div>
            {record.description && (
              <div className="max-w-xs text-sm truncate" style={pageStyles.textSecondary}>{record.description}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'owner_name',
      title: 'Owner',
      dataIndex: 'owner_name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <div className="p-1 rounded-full" style={{ backgroundColor: `${branding.textSecondaryColor}30` }}>
            <User className="w-4 h-4" style={{ color: branding.textSecondaryColor }} />
          </div>
          <div>
            <div className="text-sm font-medium" style={pageStyles.text}>{record.owner_name}</div>
            <div className="text-xs" style={pageStyles.textSecondary}>{record.owner_email}</div>
            <div className="text-xs" style={pageStyles.textSecondary}>{record.owner}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'is_active',
      title: 'Status',
      dataIndex: 'is_active',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
      render: value => (
        <div className="flex items-center space-x-2">
          <div
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${value ? 'shadow-sm' : 'border'
              }`}
            style={value ? {
              color: branding.textColor,
              backgroundColor: statusColors.success.backgroundColor,
              borderColor: statusColors.success.borderColor,
              boxShadow: `0 2px 4px ${statusColors.success.backgroundColor}40`
            } : {
              color: branding.textSecondaryColor,
              backgroundColor: `${branding.surfaceColor}60`,
              borderColor: `${branding.textSecondaryColor}30`
            }}
          >
            <div
              className={`w-2 h-2 rounded-full mr-2 ${value ? 'animate-pulse' : ''
                }`}
              style={{
                backgroundColor: value ? statusColors.success.color : branding.textSecondaryColor
              }}
            />
            {value ? 'Active' : 'Inactive'}
          </div>
        </div>
      ),
    },
    {
      key: 'currency_code',
      title: 'Currency',
      dataIndex: 'currency_code',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (value, record) =>
        value ? (
          <div className="flex items-center space-x-2">
            <div>
              <div className="text-sm font-medium text-white" style={pageStyles.text}>{record.currency_code}</div>
              {record.currency_name && (
                <div className="text-xs text-gray-300 truncate" style={pageStyles.textSecondary}>{record.currency_name}</div>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400" style={pageStyles.textSecondary}>No currency</span>
        ),
    },
    {
      key: 'website_url',
      title: 'Website',
      dataIndex: 'website_url',
      sortable: false,
      filterable: false,
      render: value =>
        value ? (
          <div className="flex items-center space-x-2">
            <div
              className="flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-200 hover:shadow-md cursor-pointer group"
              style={{
                backgroundColor: `${branding.surfaceColor}40`,
                borderColor: `${branding.textSecondaryColor}20`,
                color: branding.textColor
              }}
              onClick={() => window.open(value, '_blank', 'noopener,noreferrer')}
            >
              <div
                className="p-1.5 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: `${statusColors.info.backgroundColor}60`
                }}
              >
                <Globe className="w-3.5 h-3.5" style={{ color: statusColors.info.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: statusColors.info.color }}>
                  Visit Website
                </div>
                <div className="text-xs truncate max-w-24 opacity-70" style={{ color: branding.textSecondaryColor }}>
                  {new URL(value).hostname}
                </div>
              </div>
              <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity duration-200" style={{ color: branding.textSecondaryColor }} />
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <div
              className="flex items-center space-x-2 px-3 py-2 rounded-lg border opacity-60"
              style={{
                backgroundColor: `${branding.surfaceColor}20`,
                borderColor: `${branding.textSecondaryColor}10`,
                color: branding.textSecondaryColor
              }}
            >
              <div
                className="p-1.5 rounded-md"
                style={{
                  backgroundColor: `${branding.textSecondaryColor}20`
                }}
              >
                <Globe className="w-3.5 h-3.5" style={{ color: branding.textSecondaryColor }} />
              </div>
              <span className="text-xs font-medium">No website</span>
            </div>
          </div>
        ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      sortable: true,
      filterable: false,
      render: value => (
        <span className="text-sm" style={pageStyles.textSecondary}>
          {formatDistanceToNow(new Date(value), { addSuffix: true })}
        </span>
      ),
    },
  ]

  // Define table actions
  const actions: TableAction<SaleShop>[] = [
    {
      key: 'view',
      label: ' View',
      icon: <Eye className="w-4 h-4" style={{ color: statusColors.info.color }} />,
      onClick: record => {
        // Navigate to view page
        window.location.href = `/sale-shops/${record.id}`
      },
      className: 'hover:scale-105 transition-transform duration-200 px-3 py-2 rounded-lg font-medium text-xs',
    },
    {
      key: 'edit',
      label: ' Edit',
      icon: <Edit className="w-4 h-4" style={{ color: branding.primaryColor }} />,
      onClick: record => {
        // Navigate to edit page
        window.location.href = `/sale-shops/${record.id}`
      },
      className: 'hover:scale-105 transition-transform duration-200 px-3 py-2 rounded-lg font-medium text-xs',
    },
  ]

  return (
    <div className="min-h-screen bg-gray-900" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-4xl font-bold text-white" style={pageStyles.text}>Admin - Sales Channels</h1>
              </div>
              <p className="text-lg text-gray-300" style={pageStyles.textSecondary}>Manage all sales channels across the platform</p>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Link to="/admin/sale-channels/create">
                <Button className="flex items-center justify-center px-4 py-2 text-sm text-white hover:opacity-80 sm:px-6 sm:py-3 sm:text-base" style={pageStyles.primaryBg}>
                  <Plus className="w-4 h-4 mr-2 sm:h-5 sm:w-5" />
                  <span className="font-medium">Create Channel for User</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Total Channels
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>{totalCount}</p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>All sales channels</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <ShoppingCart className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Active Channels
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {saleShops.filter(shop => shop.is_active).length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Currently running</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Unique Owners
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {new Set(saleShops.map(shop => shop.owner_email)).size}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Different users</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <Users className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    With Webhooks
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {saleShops.filter(shop => shop.webhook_url).length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Real-time notifications</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.info.backgroundColor }}>
                  <Webhook className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                <XCircle className="w-6 h-6" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold" style={pageStyles.text}>Error loading sale shops</h3>
                <p className="mb-4 text-sm" style={{ color: statusColors.error.color }}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      fetchSaleShops({
                        page: pagination.current,
                        pageSize: pagination.pageSize,
                        sort,
                        filters,
                        search: '',
                      })
                    }
                    className="hover:opacity-80"
                    style={{ backgroundColor: statusColors.warning.backgroundColor, borderColor: statusColors.warning.borderColor, color: branding.textColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="hover:opacity-80"
                    style={{ color: statusColors.warning.color }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="transition-all duration-300 border hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
          <DataTable
            columns={columns}
            data={saleShops}
            loading={loading}
            pagination={pagination}
            filters={filters}
            sort={sort}
            actions={actions}
            rowKey="id"
            onSort={setSort}
            onFilter={setFilters}
            onPageChange={(page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }))
              fetchSaleShops({
                page,
                pageSize,
                sort,
                filters,
                search: '',
              })
            }}
            onSearch={search => {
              fetchSaleShops({
                page: 1,
                pageSize: pagination.pageSize,
                sort,
                filters,
                search,
              })
            }}
            onServerSideChange={fetchSaleShops}
            serverSide={true}
            searchable={true}
            searchPlaceholder="Search by name, description, owner, or URL..."
            emptyText="No sales channels found"
            emptyIcon={<ShoppingCart className="w-12 h-12" style={{ color: branding.textSecondaryColor }} />}
            loadingText="Loading sales channels..."
            showStats={true}
            showSearch={true}
            showFilters={true}
            showPagination={true}
            showActions={true}
            striped={true}
            hoverable={true}
            bordered={false}
            className="!bg-transparent !border-none !rounded-none"
          />
        </div>
      </div>
    </div>
  )
}
